import { useMemo, useState } from "react"

import { isInPlaceEditingActive } from "scrivito"
import { PortalNewsPageObj } from "scrivito/Objs/PortalNewsPage/PortalNewsPageObjClass"
import {
  PortalNewsPostObj,
  PortalNewsPostObjClass
} from "scrivito/Objs/PortalNewsPost/PortalNewsPostObjClass"
import { getCurrentSiteID } from "shared/lib/getCurrentSiteID"

import { getPortalNewsInRange } from "entities/portal-news/lib/getPortalNewsInRange"
import { useLastReadPortalNews } from "entities/portal-news/model/portal-news-store"

import { PortalNewsFilterFormData } from "../types/PortalNewsFilter"

interface UsePortalNewsListModelArgs extends PortalNewsFilterFormData {
  page: PortalNewsPageObj | null | undefined
}

export interface PortalNewsListData {
  posts: PortalNewsPostObj[]
  unreadPostIDs: string[]
  lastRead: Date | null
  page: PortalNewsPageObj | null | undefined
}

export function usePortalNewsListData({
  page,
  searchTerm
}: UsePortalNewsListModelArgs): PortalNewsListData {
  const [now] = useState(new Date())

  const { lastRead } = useLastReadPortalNews()

  const oldestPostThreshold = useMemo(() => {
    if (page?.get("maxPostAgeInDays")) {
      const next = new Date()
      next.setDate(now.getDate() - (page.get("maxPostAgeInDays") ?? 30))
      return next
    }
    return null
  }, [page?.get("maxPostAgeInDays")])

  const allPosts = getPortalNewsInRange(
    isInPlaceEditingActive() ? null : oldestPostThreshold,
    isInPlaceEditingActive() ? null : now,
    page?.path()
  )
    .order("date", "desc")
    .toArray()

  const posts = useMemo(() => {
    if (searchTerm) {
      const matchedTitle = PortalNewsPostObjClass.onSite(getCurrentSiteID())
        .where("title", "contains", searchTerm)
        .toArray()
      const matchedText = PortalNewsPostObjClass.onSite(getCurrentSiteID())
        .where("text", "contains", searchTerm)
        .toArray()
      return [...matchedTitle, ...matchedText]
    }

    return allPosts
  }, [allPosts, searchTerm])

  const unreadPostIDs = posts
    .filter(post => {
      const postedDate = post.get("date")
      if (!lastRead) {
        return true
      }
      return postedDate ? postedDate.getTime() > lastRead.getTime() : false
    })
    .map(post => post.id())

  return { posts, lastRead, unreadPostIDs, page }
}
